import { useState, useCallback } from "react"
import { User } from "../ZeefTypes"

interface UseZeefMentionsReturn {
  // State
  showMentionDropdown: boolean
  mentionQuery: string
  mentionSuggestions: User[]
  mentionCursorPosition: number

  // Actions
  checkForMention: (text: string, cursorPosition: number, availableUsers: User[]) => void
  handleMentionSelect: (
    user: User, 
    inputMessage: string, 
    onInputChange: (newMessage: string) => void,
    messageInputRef: React.RefObject<HTMLInputElement>
  ) => void
  handleMentionKeyDown: (event: React.KeyboardEvent) => boolean // Returns true if event was handled
  hideMentionDropdown: () => void
}

export const useZeefMentions = (): UseZeefMentionsReturn => {
  const [showMentionDropdown, setShowMentionDropdown] = useState(false)
  const [mentionQuery, setMentionQuery] = useState("")
  const [mentionSuggestions, setMentionSuggestions] = useState<User[]>([])
  const [mentionCursorPosition, setMentionCursorPosition] = useState(0)

  const filterMentionSuggestions = useCallback((query: string, availableUsers: User[]) => {
    // Remove duplicates and filter by query
    const uniqueUsers = Array.from(new Map(availableUsers.map((user) => [user.id, user])).values())
    const filtered = uniqueUsers
      .filter((user) => user.username.toLowerCase().includes(query.toLowerCase()))
      .slice(0, 5) // Limit to 5 suggestions

    setMentionSuggestions(filtered)
  }, [])

  const checkForMention = useCallback((text: string, cursorPosition: number, availableUsers: User[]) => {
    setMentionCursorPosition(cursorPosition)

    // Find if there's an @ before the cursor
    const beforeCursor = text.substring(0, cursorPosition)
    const lastAtIndex = beforeCursor.lastIndexOf("@")

    if (lastAtIndex !== -1) {
      // Check if the @ is at the start or after a space
      const charBeforeAt = lastAtIndex > 0 ? beforeCursor[lastAtIndex - 1] : " "
      if (charBeforeAt === " " || lastAtIndex === 0) {
        // Extract the query after @
        const query = beforeCursor.substring(lastAtIndex + 1)

        // Check if there's a space after @, if so, close dropdown
        if (query.includes(" ")) {
          setShowMentionDropdown(false)
          return
        }

        // Show dropdown and filter suggestions
        setShowMentionDropdown(true)
        setMentionQuery(query)
        filterMentionSuggestions(query, availableUsers)
        return
      }
    }

    // No @ found or invalid position, hide dropdown
    setShowMentionDropdown(false)
  }, [filterMentionSuggestions])

  const handleMentionSelect = useCallback((
    user: User,
    inputMessage: string,
    onInputChange: (newMessage: string) => void,
    messageInputRef: React.RefObject<HTMLInputElement>
  ) => {
    const beforeCursor = inputMessage.substring(0, mentionCursorPosition)
    const afterCursor = inputMessage.substring(mentionCursorPosition)

    // Find the @ position
    const lastAtIndex = beforeCursor.lastIndexOf("@")

    if (lastAtIndex !== -1) {
      const beforeAt = inputMessage.substring(0, lastAtIndex)
      const newMessage = beforeAt + `@${user.username} ` + afterCursor

      onInputChange(newMessage)
      setShowMentionDropdown(false)
      setMentionQuery("")
      setMentionSuggestions([])

      // Focus back to input and set cursor position
      setTimeout(() => {
        if (messageInputRef.current) {
          messageInputRef.current.focus()
          const newCursorPos = lastAtIndex + user.username.length + 2
          messageInputRef.current.setSelectionRange(newCursorPos, newCursorPos)
        }
      }, 0)
    }
  }, [mentionCursorPosition])

  const handleMentionKeyDown = useCallback((event: React.KeyboardEvent): boolean => {
    // Handle escape key first to prevent modal closure
    if (event.key === "Escape") {
      if (showMentionDropdown) {
        event.preventDefault()
        event.stopPropagation()
        setShowMentionDropdown(false)
        return true
      }
    }

    if (showMentionDropdown && mentionSuggestions.length > 0) {
      if (event.key === "Enter" || event.key === "Tab") {
        // This would need to be handled by the parent component
        // since it needs access to input state and refs
        event.preventDefault()
        return true
      }
    }

    return false
  }, [showMentionDropdown, mentionSuggestions])

  const hideMentionDropdown = useCallback(() => {
    setShowMentionDropdown(false)
    setMentionQuery("")
    setMentionSuggestions([])
  }, [])

  return {
    // State
    showMentionDropdown,
    mentionQuery,
    mentionSuggestions,
    mentionCursorPosition,

    // Actions
    checkForMention,
    handleMentionSelect,
    handleMentionKeyDown,
    hideMentionDropdown,
  }
}
